import { BagPosterProps, Size } from "~/types/cart";
import { Link, useNavigate } from "@remix-run/react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { dataset, projectId } from "~/sanity/projectDetails";

import { Button } from "../ui/button";
import { posterSizesMapper } from "~/utils/constants";
import urlBuilder from "@sanity/image-url";
import { useMarketCartState } from "~/utils";
import { useModalStore } from "~/utils/stores/modalStore";

const CartModal = ({
  locale,
  currencySymbol,
}: {
  locale: string;
  currencySymbol: string;
}) => {
  let navigate = useNavigate();
  const useCartState = useMarketCartState(locale);

  const posters = useCartState((state) => state.cart);

  const modals = useModalStore((s) => s.modals);
  const closeModal = useModalStore((s) => s.closeModal);

  const removeItem = useCartState((state) => state.removeFromCart);
  const totalPrice = useCartState((state) => state.totalPrice);
  const totalItems = useCartState((state) => state.totalItems);

  const handleBrowseButton = () => {
    closeModal("cart");
    navigate(`/${locale}/posters`);
  };

  return (
    <div>
      <Sheet open={!!modals["cart"]} onOpenChange={() => closeModal("cart")}>
        <SheetContent className="flex flex-col justify-between h-full min-w-full md:min-w-[400px]">
          <SheetHeader className="py-4 border-b-[1px] border-black border-solid">
            <SheetTitle className="text-2xl font-masonExtendedBold text-left">
              {locale === "se" ? "Varukorg" : "Shopping Bag"}
              <span className="border-[1px] border-gray-500 ml-4 p-2 text-sm rounded-full text-gray-600 align-middle">
                {totalItems}
              </span>
            </SheetTitle>
          </SheetHeader>

          <div className="grid overflow-scroll flex-1">
            {posters.length === 0 ? (
              <div>
                <p className="font-thin">
                  {locale === "se"
                    ? "Din varukorg är tom"
                    : "Your shopping bag is empty"}
                </p>
              </div>
            ) : (
              <ul className="grid content-start gap-8 list-none pb-8">
                {posters.map((poster: BagPosterProps) => {
                  const imgSrcUrl = urlBuilder({ projectId, dataset })
                    .image(poster.image)
                    .width(900)
                    .fit("max")
                    .auto("format")
                    .quality(100)
                    .url();
                  return (
                    <li key={poster.variation.gProductId}>
                      <article
                        className="grid gap-5"
                        style={{
                          gridTemplateColumns: "128px 1fr",
                          gridTemplateRows: "164px",
                        }}
                      >
                        <SheetClose asChild>
                          <Link to={`${locale}/posters/${poster.slug}`}>
                            <img src={imgSrcUrl} alt={poster.image.alt} />
                          </Link>
                        </SheetClose>

                        <div className="grid gap-2 ">
                          <div>
                            <SheetClose asChild>
                              <Link
                                to={`${locale}/posters/${poster.slug}`}
                                className="font-masonExtendedBold underline border-none "
                              >
                                {poster.name}
                              </Link>
                            </SheetClose>

                            <div
                              key={poster.variation.size}
                              className="grid items-end text-xs text-gray-500 mt-2"
                            >
                              <span>
                                {
                                  posterSizesMapper[
                                    poster.variation.size as Size
                                  ]
                                }
                              </span>

                              <span>
                                {locale === "se" ? "Antal: " : "Quantity: "}
                                {poster.quantity}
                              </span>
                              <span>
                                {locale === "se"
                                  ? `${poster.variation.price} ${currencySymbol}`
                                  : `${currencySymbol}${poster.variation.price}`}
                              </span>
                            </div>

                            <div className="grid items-end mt-5 lg:mt-8">
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-20"
                                onClick={() => removeItem(poster)}
                              >
                                {locale === "se" ? "Ta bort" : "Remove"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <SheetFooter>
            {totalItems === 0 ? (
              <div className="flex flex-col gap-4 w-full">
                <Button className="w-full" onClick={handleBrowseButton}>
                  {locale === "se" ? "Gå till galleriet" : "Browse gallery"}
                </Button>

                <SheetClose asChild>
                  <Button variant="outline" size="sm">
                    {locale === "se" ? "Stäng" : "Close"}
                  </Button>
                </SheetClose>
              </div>
            ) : (
              <div className="flex flex-col gap-4 w-full">
                <div className="flex justify-between border-t-[1px] border-black border-solid pt-2 font-bold">
                  <h3>{locale === "se" ? "Totalt" : "Total"}</h3>
                  <span>
                    {locale === "se"
                      ? `${Number(totalPrice).toFixed(2)} ${currencySymbol}`
                      : `${currencySymbol}${Number(totalPrice).toFixed(2)}`}
                  </span>
                </div>

                <SheetClose asChild>
                  <Button variant="default" size="lg" asChild>
                    <Link to={`${locale}/checkout`}>
                      {locale === "se" ? "Till kassan" : "Checkout"}
                    </Link>
                  </Button>
                </SheetClose>

                <SheetClose asChild>
                  <Button variant="outline" size="sm">
                    {locale === "se" ? "Stäng" : "Close"}
                  </Button>
                </SheetClose>
              </div>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default CartModal;
